import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Navbar from '../components/Navbar'
import Onboarding from './Onboarding'

export default function ClinicianDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [showCodes, setShowCodes] = useState(false)
  const [showInvite, setShowInvite] = useState(null)
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteSent, setInviteSent] = useState(false)
  const [patientCount, setPatientCount] = useState(null)
  const [onboardingDone, setOnboardingDone] = useState(false)

  useEffect(() => {
    if (profile?.clinic_id) {
      supabase.from('users')
        .select('id', { count: 'exact', head: true })
        .eq('clinic_id', profile.clinic_id)
        .eq('role', 'patient')
        .then(({ count }) => setPatientCount(count || 0))
    }
  }, [profile])

  function resetInvite() {
    setShowInvite(null)
    setInviteName('')
    setInviteEmail('')
    setInviteError('')
    setInviteSent(false)
  }

  async function handleInvite(e) {
    e.preventDefault()
    setInviting(true)
    setInviteError('')
    const endpoint = showInvite === 'patient'
      ? '/.netlify/functions/invite-patient'
      : '/.netlify/functions/invite-clinician'
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: inviteName, email: inviteEmail, clinic_id: profile.clinic_id })
      })
      const data = await res.json()
      if (!res.ok) { setInviteError(data.error || 'Something went wrong'); setInviting(false); return }
      setInviteSent(true)
    } catch {
      setInviteError('Failed to send invite. Please try again.')
    }
    setInviting(false)
  }

  const daysLeft = (() => {
    // handled by AuthContext
    return 0
  })()

  if (patientCount === null) return null

  if (patientCount === 0 && !onboardingDone) {
    return <Onboarding onComplete={() => setOnboardingDone(true)} />
  }

  return (
    <>
      <Navbar />
      <div className="page">
        <div className="page-header">
          <h1>{profile?.clinics?.name}</h1>
          <p>Welcome back, {profile?.full_name}</p>
        </div>

        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: '1rem' }}>
          <p style={{ fontSize: 13, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
            <strong>Beta program:</strong> Please do not enter real patient data. This app is still in testing.
          </p>
        </div>

        <div className="card card-hover" onClick={() => navigate('/clinician/patients')} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>👥</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 16 }}>Patients</p>
            <p style={{ fontSize: 13 }}>Assign programs and track compliance</p>
          </div>
          <span style={{ color: 'var(--brand)', fontSize: 20 }}>→</span>
        </div>

        <div className="card card-hover" onClick={() => navigate('/clinician/compliance')} style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📊</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 16 }}>Clinic compliance</p>
            <p style={{ fontSize: 13 }}>Summary view across all active patients</p>
          </div>
          <span style={{ color: 'var(--brand)', fontSize: 20 }}>→</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: '2rem' }}>
          <div className="card card-hover" onClick={() => navigate('/clinician/exercises')}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: '0.75rem' }}>🏋️</div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 15 }}>Exercises</p>
            <p style={{ fontSize: 13 }}>Upload and manage</p>
          </div>
          <div className="card card-hover" onClick={() => navigate('/clinician/interventions')}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: '0.75rem' }}>✅</div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 15 }}>Tasks</p>
            <p style={{ fontSize: 13 }}>Manage and assign</p>
          </div>
          <div className="card card-hover" onClick={() => navigate('/clinician/diagnoses')}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fdf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: '0.75rem' }}>📋</div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 15 }}>Programs</p>
            <p style={{ fontSize: 13 }}>Templates and protocols</p>
          </div>
          <div className="card card-hover" onClick={() => navigate('/clinician/compliance')}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: '0.75rem' }}>📊</div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 15 }}>Compliance</p>
            <p style={{ fontSize: 13 }}>All active patients</p>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button type="button" className="secondary" onClick={() => setShowInvite('patient')} style={{ width: '100%', padding: '12px 14px', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>📧 Invite a patient</span>
            <span style={{ fontSize: 18 }}>→</span>
          </button>
          <button type="button" className="secondary" onClick={() => setShowInvite('clinician')} style={{ width: '100%', padding: '12px 14px', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>👩‍⚕️ Invite a clinician</span>
            <span style={{ fontSize: 18 }}>→</span>
          </button>


        </div>
      </div>

      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) resetInvite() }}>
          <div style={{ background: 'var(--surface)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, overflow: 'hidden' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 17, margin: 0 }}>
                {showInvite === 'patient' ? 'Invite a patient' : 'Invite a clinician'}
              </h2>
              <button type="button" className="secondary" onClick={resetInvite} style={{ fontSize: 13, padding: '6px 12px' }}>Cancel</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {inviteSent ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <div style={{ fontSize: 48, marginBottom: '1rem' }}>📧</div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16, marginBottom: 8 }}>Invite sent!</p>
                  <p style={{ fontSize: 14, marginBottom: '1.5rem' }}>{inviteName} will receive an email to set up their account.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setInviteSent(false); setInviteName(''); setInviteEmail('') }} style={{ flex: 1 }}>Invite another</button>
                    <button className="secondary" onClick={resetInvite} style={{ flex: 1 }}>Done</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleInvite} className="form-group">
                  {inviteError && <div className="alert-error">{inviteError}</div>}
                  {showInvite === 'clinician' && (
                    <div style={{ background: 'var(--brand-light)', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 14px' }}>
                      <p style={{ fontSize: 12, color: 'var(--brand)', margin: '0 0 4px', fontWeight: 600 }}>Clinician invite code</p>
                      <p style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: 'var(--brand)', letterSpacing: 2, margin: 0 }}>{profile?.clinics?.clinician_invite_code}</p>
                      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 0' }}>Share this if they prefer to sign up at completept.app/signup/clinician</p>
                    </div>
                  )}
                  <div className="form-field">
                    <label className="form-label">{showInvite === 'patient' ? "Patient" : "Clinician"} full name</label>
                    <input placeholder="Jane Smith" value={inviteName} onChange={e => setInviteName(e.target.value)} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{showInvite === 'patient' ? "Patient" : "Clinician"} email address</label>
                    <input type="email" placeholder="jane@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
                  </div>
                  <button type="submit" disabled={inviting} style={{ padding: 14, fontSize: 15 }}>
                    {inviting ? 'Sending invite...' : 'Send invite'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}